/**
 * Schemas de entrada de `/meta-ads` — validação Zod, consumida pelo `PipeZod`.
 *
 * Duas regras deste arquivo:
 *
 *  1. **O período é resolvido no servidor.** O cliente manda `periodo=7d`, não um par
 *     de datas calculado no navegador. O motivo é o RN-55: "hoje" e "últimos 7 dias"
 *     são dias civis de São Paulo, e o relógio do navegador do gestor pode estar em
 *     qualquer fuso — dois usuários veriam números diferentes para o mesmo filtro.
 *     `PERSONALIZADO` é a única forma que aceita datas, e aí elas são explícitas.
 *  2. **Nada de token, secret ou id de conta externa vindo do cliente.** A conta é
 *     referenciada pelo `id` interno; o `act_…` nunca trafega como entrada. §23.
 */
import { z } from 'zod'

import { zDataFiltro, zIdCatalogo } from '../comum/campos'
import {
  ACOES_OTIMIZACAO_META,
  NIVEIS_ANUNCIO,
  OPERACOES_ANUNCIO,
  STATUS_RECOMENDACAO_META,
} from './enums'

/**
 * Períodos oferecidos pela TELA-META-01.
 *
 * `HOJE` está na lista, mas com uma ressalva que a interface precisa mostrar: insight
 * do dia corrente na Meta é parcial e muda ao longo do dia. Serve para acompanhar
 * entrega, não para decidir orçamento.
 */
export const PERIODOS_META_ADS = ['HOJE', 'ONTEM', '7D', '14D', '30D', 'PERSONALIZADO'] as const
export type PeriodoMetaAds = (typeof PERIODOS_META_ADS)[number]

/** Quantos dias cada período cobre. `PERSONALIZADO` e `HOJE`/`ONTEM` são tratados fora. */
export const DIAS_POR_PERIODO: Readonly<Record<PeriodoMetaAds, number>> = {
  HOJE: 1,
  ONTEM: 1,
  '7D': 7,
  '14D': 14,
  '30D': 30,
  PERSONALIZADO: 0,
}

/**
 * Teto do período personalizado.
 *
 * 180 dias: uma consulta de dois anos em `meta_insights` no nível de anúncio varreria
 * centenas de milhares de linhas para responder um card, e nenhuma decisão de tráfego
 * se toma com essa janela. Quem precisar de série longa usa exportação, não dashboard.
 */
export const MAXIMO_DIAS_PERSONALIZADO = 180

export const schemaFiltrosMetaAds = z
  .object({
    /** `id` interno da conta. Ausente = todas as contas ativas (§1 da missão). */
    contaId: zIdCatalogo.optional(),
    periodo: z.enum(PERIODOS_META_ADS).default('7D'),
    de: zDataFiltro.optional(),
    ate: zDataFiltro.optional(),
    operacao: z.enum(OPERACOES_ANUNCIO).optional(),
  })
  .superRefine((valor, contexto) => {
    if (valor.periodo !== 'PERSONALIZADO') {
      return
    }

    if (valor.de === undefined || valor.ate === undefined) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['de'],
        message: 'Período personalizado exige `de` e `ate`.',
      })
      return
    }

    if (valor.de > valor.ate) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['de'],
        message: 'A data inicial não pode ser posterior à final.',
      })
      return
    }

    // Comparação por string ISO funciona para ordenar, mas não para contar dias — e
    // aqui o limite precisa ser em dias. `Date.UTC` sobre as partes evita o
    // deslocamento de fuso que `new Date('2026-08-01')` introduz.
    const dias = diasEntreIso(valor.de, valor.ate)
    if (dias > MAXIMO_DIAS_PERSONALIZADO) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ate'],
        message: `O período personalizado é limitado a ${MAXIMO_DIAS_PERSONALIZADO} dias.`,
      })
    }
  })

export type FiltrosMetaAds = z.infer<typeof schemaFiltrosMetaAds>

/** Filtros da hierarquia — recorta por nível pai. */
export const schemaFiltrosHierarquia = z.object({
  contaId: zIdCatalogo.optional(),
  periodo: z.enum(PERIODOS_META_ADS).default('7D'),
  de: zDataFiltro.optional(),
  ate: zDataFiltro.optional(),
  /** Id externo da campanha, para listar conjuntos dela. */
  campanhaId: z.string().trim().min(1).max(120).optional(),
  /** Id externo do conjunto, para listar anúncios dele. */
  conjuntoId: z.string().trim().min(1).max(120).optional(),
  /** `true` esconde pausados e arquivados. */
  apenasAtivos: z.enum(['true', 'false']).transform((valor) => valor === 'true').optional(),
})

export type FiltrosHierarquia = z.infer<typeof schemaFiltrosHierarquia>

export const schemaFiltrosRecomendacoes = z.object({
  contaId: zIdCatalogo.optional(),
  status: z.enum(STATUS_RECOMENDACAO_META).default('ABERTA'),
  nivel: z.enum(NIVEIS_ANUNCIO).optional(),
})

export type FiltrosRecomendacoes = z.infer<typeof schemaFiltrosRecomendacoes>

// ---------------------------------------------------------------------------
// Cadastro de conta
// ---------------------------------------------------------------------------

/**
 * `conta_externa_id` aceita `act_123` ou `123`.
 *
 * Aceita as duas formas porque é o que o gestor tem à mão: o Gerenciador de Anúncios
 * mostra o número puro em alguns lugares e `act_` prefixado em outros, e recusar uma
 * delas geraria um erro de validação que a pessoa não sabe corrigir. A normalização
 * para a forma canônica acontece no serviço, num lugar só.
 */
export const zContaExternaId = z
  .string()
  .trim()
  .regex(/^(act_)?\d{5,25}$/, 'Informe o ID da conta de anúncio (act_123456789 ou 123456789).')

export const schemaCriarContaAnuncio = z.object({
  contaExternaId: zContaExternaId,
  nome: z.string().trim().min(2).max(160),
  operacao: z.enum(OPERACOES_ANUNCIO).default('OUTRA'),
  ativo: z.boolean().default(true),
  /** CPL alvo em reais, para o painel comparar contra a meta do negócio. */
  cplAlvo: z.number().positive().max(100_000).nullable().optional(),
})

export type EntradaCriarContaAnuncio = z.infer<typeof schemaCriarContaAnuncio>

export const schemaAtualizarContaAnuncio = schemaCriarContaAnuncio
  .partial()
  // `contaExternaId` fora do update: trocar o id externo de uma conta cadastrada não
  // é edição, é outra conta — e arrastaria consigo insights, recomendações e
  // histórico de otimização de uma conta para outra. Quem errou o id apaga e cadastra.
  .omit({ contaExternaId: true })

export type EntradaAtualizarContaAnuncio = z.infer<typeof schemaAtualizarContaAnuncio>

// ---------------------------------------------------------------------------
// Ações de otimização — §16
// ---------------------------------------------------------------------------

/**
 * Pedido de execução de uma ação na Meta.
 *
 * `confirmado` é obrigatório e tem de ser `true`. Um booleano exigido no corpo não é
 * segurança — é **intenção**: garante que nenhuma ação na conta de anúncio possa ser
 * disparada por um clique acidental, por um retry de rede ou por uma chamada montada a
 * partir da documentação sem ler a consequência. §11 e §20 da missão pedem confirmação
 * explícita, e este campo é onde ela vive no contrato.
 */
export const schemaExecutarAcao = z
  .object({
    contaId: zIdCatalogo,
    nivel: z.enum(NIVEIS_ANUNCIO),
    /** Id externo do alvo na Meta. */
    idExterno: z.string().trim().min(1).max(120),
    acao: z.enum(ACOES_OTIMIZACAO_META),
    /** Recomendação que originou a ação, quando houve uma. */
    recomendacaoId: z.coerce.number().int().positive().optional(),
    motivo: z.string().trim().min(10).max(1000),
    confirmado: z.literal(true, {
      errorMap: () => ({
        message: 'A ação exige confirmação explícita (`confirmado: true`).',
      }),
    }),
    /** Novo orçamento diário, em reais. Só para `ALTERAR_ORCAMENTO`. */
    orcamentoDiario: z.number().positive().max(1_000_000).optional(),
  })
  .superRefine((valor, contexto) => {
    if (valor.acao === 'ALTERAR_ORCAMENTO' && valor.orcamentoDiario === undefined) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['orcamentoDiario'],
        message: 'Alterar orçamento exige o novo valor diário.',
      })
    }

    // Orçamento não existe no nível do anúncio na Meta — ele é do conjunto ou da
    // campanha. Recusar aqui evita uma chamada à Graph API que voltaria com erro
    // genérico e um registro de ação `FALHOU` sem causa legível.
    if (valor.acao === 'ALTERAR_ORCAMENTO' && valor.nivel === 'ANUNCIO') {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nivel'],
        message: 'Orçamento é definido no conjunto ou na campanha, nunca no anúncio.',
      })
    }
  })

export type EntradaExecutarAcao = z.infer<typeof schemaExecutarAcao>

/** Descarte de recomendação — sai da lista sem virar ação na Meta. */
export const schemaDescartarRecomendacao = z.object({
  motivo: z.string().trim().min(3).max(500).optional(),
})

export type EntradaDescartarRecomendacao = z.infer<typeof schemaDescartarRecomendacao>

// ---------------------------------------------------------------------------
// Otimizador e variações — §12 e §15
// ---------------------------------------------------------------------------

export const schemaPerguntarOtimizador = z.object({
  pergunta: z.string().trim().min(5).max(500),
  contaId: zIdCatalogo.optional(),
  periodo: z.enum(PERIODOS_META_ADS).default('7D'),
})

export type EntradaPerguntarOtimizador = z.infer<typeof schemaPerguntarOtimizador>

export const schemaGerarVariacao = z.object({
  /** Id externo do anúncio que serve de base. */
  anuncioIdExterno: z.string().trim().min(1).max(120),
  /** Direção que o gestor quer testar ("mais direto", "focar em urgência"). */
  orientacao: z.string().trim().min(3).max(500).optional(),
})

export type EntradaGerarVariacao = z.infer<typeof schemaGerarVariacao>

export const schemaDecidirVariacao = z.object({
  decisao: z.enum(['APROVAR', 'REJEITAR']),
  observacao: z.string().trim().max(500).optional(),
})

export type EntradaDecidirVariacao = z.infer<typeof schemaDecidirVariacao>

// ---------------------------------------------------------------------------
// Internos
// ---------------------------------------------------------------------------

/**
 * Dias entre duas datas `YYYY-MM-DD`, inclusivo.
 *
 * Aritmética em UTC sobre as partes, e não `new Date(iso)`: a segunda forma
 * interpreta `'2026-08-01'` como meia-noite UTC e, num servidor em -03:00, `getDate()`
 * devolve 31 de julho. Com só três campos numéricos, `Date.UTC` é exato.
 */
export function diasEntreIso(de: string, ate: string): number {
  const inicio = paraUtc(de)
  const fim = paraUtc(ate)
  if (inicio === null || fim === null) return 0
  return Math.floor((fim - inicio) / 86_400_000) + 1
}

function paraUtc(iso: string): number | null {
  const partes = iso.split('-')
  if (partes.length !== 3) return null
  const [ano, mes, dia] = partes.map((parte) => Number.parseInt(parte, 10))
  if (ano === undefined || mes === undefined || dia === undefined) return null
  if (!Number.isFinite(ano) || !Number.isFinite(mes) || !Number.isFinite(dia)) return null
  return Date.UTC(ano, mes - 1, dia)
}
